// Contract of the Power admin routes for the DLQ inbox (Operações): the events the outbox relay
// parked after its attempts ran out, newest failure first across every tenant, and the two ways
// out, a replay into the outbox or a discard for good. Nothing here carries a raw payload or a raw
// error: Power answers the payload as a redacted view and the last error scrubbed of personal
// data, tokens and credentials. `@finnest/events` owns the stored row and the cursor; this
// package reaches the browser, so the wire shapes are declared here and the cursor is opaque.
//
// The structs are closed contracts: decode them with `{ onExcessProperty: 'error' }`.
import { Schema } from 'effect';
import { IsoDateTimeUtc } from '@finnest/core/effect';

/** At most this many events per page, the store's own bound. */
export const DLQ_INBOX_PAGE_MAX = 100;

/** The page size a list that names none is answered with. */
export const DLQ_INBOX_PAGE_DEFAULT = 50;

/** At most this many events per replay request. */
export const DLQ_REPLAY_BATCH_MAX = 50;

/**
 * An event that occurred more than this long ago (24 hours, counted from its `occurredAt`) is
 * stale: replaying it needs the operator's acknowledgement, since consumers see it late.
 */
export const DLQ_STALE_AFTER_MS = 86_400_000;

/** The bounds of a discard's reason, in characters. */
export const DLQ_DISCARD_REASON_MIN = 10;
export const DLQ_DISCARD_REASON_MAX = 500;

const EVENT_NAME_MAX = 255;
const CURSOR_MAX = 512;

/**
 * The tenant a parked event belongs to, as the store's key takes it: non-empty text, unbounded
 * like the events tables' column, so every parked row can be replayed or discarded.
 */
export const DlqTenantId = Schema.NonEmptyString;

/** A parked event's id: the UUID of the outbox event that failed. */
export const DlqEventIdView = Schema.String.check(Schema.isUUID());

/**
 * The UUID of one parked row. An event replayed and parked again is a new row under the same
 * tenant and event id, so a replay or discard names the row the operator saw: one for a row
 * already replayed or discarded is `not-found`, never the event's next parking.
 */
export const DlqRowIdView = Schema.String.check(Schema.isUUID());

/**
 * Where the next page starts, opaque to the client: Power reads it back and refuses one it did
 * not write (400). Base64url text.
 */
export const DlqCursorToken = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(CURSOR_MAX),
  Schema.isBase64Url(),
);
export type DlqCursorToken = typeof DlqCursorToken.Type;

/** The list's query: the page size (1 to 100, 50 by default), one event name, the page cursor. */
export const DlqInboxQuery = Schema.Struct({
  limit: Schema.optionalKey(
    Schema.NumberFromString.check(
      Schema.isInt(),
      Schema.isBetween({ minimum: 1, maximum: DLQ_INBOX_PAGE_MAX }),
    ),
  ),
  eventName: Schema.optionalKey(
    Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(EVENT_NAME_MAX)),
  ),
  cursor: Schema.optionalKey(DlqCursorToken),
});
export type DlqInboxQuery = typeof DlqInboxQuery.Type;

/**
 * A record as an operator sees it (a parked payload, a PCM call): every value under a
 * personal-data key replaced, every string scrubbed, and `truncated` when the view was cut to its
 * size or depth bound.
 */
export const RedactedView = Schema.Struct({
  value: Schema.Json,
  truncated: Schema.Boolean,
});
export type RedactedView = typeof RedactedView.Type;

/**
 * One parked event. `idempotencyKey` and `lastError` are scrubbed, and `lastError` is cut to at
 * most 64 KiB of UTF-8 (`lastErrorTruncated` when it was); `ageMs` counts from `occurredAt`. The
 * tenant and event name are what the events tables keep, unbounded text, so no stored row breaks
 * a page.
 */
export const DlqInboxItem = Schema.Struct({
  dlqId: DlqRowIdView,
  tenantId: Schema.String,
  eventName: Schema.String,
  eventId: DlqEventIdView,
  idempotencyKey: Schema.String,
  occurredAt: IsoDateTimeUtc,
  failedAt: IsoDateTimeUtc,
  attempts: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  lastError: Schema.String,
  /** Whether `lastError` was cut to its size bound. */
  lastErrorTruncated: Schema.Boolean,
  ageMs: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  payload: RedactedView,
  /** The W3C trace context the event was written under; null for one written outside a span. */
  traceparent: Schema.OptionFromNullOr(Schema.String),
}).annotate({
  title: 'PowerAdminDlqInboxItem',
  description: 'A parked outbox event, its payload redacted and its last error scrubbed.',
});
export type DlqInboxItem = typeof DlqInboxItem.Type;

/** One page, newest failure first, and where the next starts (null on the last page). */
export const DlqInboxPage = Schema.Struct({
  items: Schema.Array(DlqInboxItem).check(Schema.isMaxLength(DLQ_INBOX_PAGE_MAX)),
  next: Schema.OptionFromNullOr(DlqCursorToken),
}).annotate({
  title: 'PowerAdminDlqInboxPage',
  description: 'One page of parked outbox events across every tenant.',
});
export type DlqInboxPage = typeof DlqInboxPage.Type;

/** A parked row: the event's key in the DLQ, and the id of the row the operator selected. */
export const DlqEventRef = Schema.Struct({
  tenantId: DlqTenantId,
  eventId: DlqEventIdView,
  dlqId: DlqRowIdView,
});
export type DlqEventRef = typeof DlqEventRef.Type;

/**
 * Body of the replay route: 1 to 50 events, each replayed in its own transaction, and whether the
 * operator acknowledges replaying events older than `DLQ_STALE_AFTER_MS`.
 */
export const DlqReplayRequest = Schema.Struct({
  items: Schema.NonEmptyArray(DlqEventRef).check(Schema.isMaxLength(DLQ_REPLAY_BATCH_MAX)),
  acknowledgeStale: Schema.Boolean,
}).annotate({
  title: 'PowerAdminDlqReplayRequest',
  description: 'The parked events to move back into the outbox.',
});
export type DlqReplayRequest = typeof DlqReplayRequest.Type;

/**
 * What became of one event: moved back into the outbox and audited (`replayed`); that row no
 * longer parked, already replayed or discarded by someone else, even when the event has parked
 * again as a new row (`not-found`); older than 24 hours without the
 * acknowledgement, left parked (`stale-unacknowledged`); or left parked because the store or the
 * audit ledger failed (`unavailable`, retry later).
 */
export const DlqReplayOutcome = Schema.Literals([
  'replayed',
  'not-found',
  'stale-unacknowledged',
  'unavailable',
]);
export type DlqReplayOutcome = typeof DlqReplayOutcome.Type;

/** One outcome per requested event, in request order. */
export const DlqReplayResults = Schema.Struct({
  results: Schema.Array(
    Schema.Struct({
      tenantId: Schema.String,
      eventId: DlqEventIdView,
      outcome: DlqReplayOutcome,
    }),
  ).check(Schema.isMaxLength(DLQ_REPLAY_BATCH_MAX)),
}).annotate({
  title: 'PowerAdminDlqReplayResults',
  description: 'What became of each requested event, in request order.',
});
export type DlqReplayResults = typeof DlqReplayResults.Type;

/**
 * Body of the discard route: the parked row, and why it is dropped for good (10 to 500
 * characters).
 */
export const DlqDiscardRequest = Schema.Struct({
  tenantId: DlqTenantId,
  eventId: DlqEventIdView,
  dlqId: DlqRowIdView,
  reason: Schema.String.check(
    Schema.isMinLength(DLQ_DISCARD_REASON_MIN),
    Schema.isMaxLength(DLQ_DISCARD_REASON_MAX),
  ),
}).annotate({
  title: 'PowerAdminDlqDiscardRequest',
  description: 'The parked event to delete for good, and why.',
});
export type DlqDiscardRequest = typeof DlqDiscardRequest.Type;

/**
 * The Problem `detail` of the 404 a discard whose row is no longer parked is answered with,
 * as the shared edge renders a missing `dlq-event`.
 */
export const dlqEventNotFoundDetail = (eventId: string): string => `dlq-event ${eventId} not found`;
